import { apiClient } from '../client';

export interface OrganizationAiContextStructured {
  business_name: string;
  website: string;
  instagram_handle: string;
  services: string;
  support_email: string;
  support_phone: string;
  notes: string;
}

export interface OrganizationAiContextResponse {
  hasContent: boolean;
  raw: string;
  parsed: OrganizationAiContextStructured;
}

export type OrganizationApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; errors?: Record<string, string> };

const empty: OrganizationAiContextStructured = {
  business_name: '',
  website: '',
  instagram_handle: '',
  services: '',
  support_email: '',
  support_phone: '',
  notes: '',
};

function safeAiContextResponse(payload: any): OrganizationAiContextResponse {
  const parsed = (payload?.parsed && typeof payload.parsed === 'object')
    ? { ...empty, ...payload.parsed }
    : { ...empty };
  return {
    hasContent: Boolean(payload?.hasContent),
    raw: typeof payload?.raw === 'string' ? payload.raw : '',
    parsed,
  };
}

export const organizationApi = {
  async getAiContext(): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.get('/organizations/me/ai-context');
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to load organization context' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(payload) };
  },

  async updateAiContext(
    payload: Partial<OrganizationAiContextStructured>,
  ): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.put('/organizations/me/ai-context', payload);
    if (res?.error || res?.success === false) {
      const errors =
        (res?.errors && typeof res.errors === 'object' ? res.errors : undefined) ??
        (res?.data?.errors && typeof res.data.errors === 'object' ? res.data.errors : undefined);
      return {
        ok: false,
        error: res?.error || 'Failed to update organization context',
        errors,
      };
    }
    const body = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(body) };
  },

  async clearAiContext(): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    const res: any = await apiClient.delete('/organizations/me/ai-context');
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to clear organization context' };
    }
    return { ok: true, data: { hasContent: false, raw: '', parsed: {} as OrganizationAiContextStructured } };
  },
};

export const EMPTY_ORG_AI_CONTEXT: OrganizationAiContextStructured = empty;

export const workspaceAiContextApi = {
  async getAiContext(workspaceId: string): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const res: any = await apiClient.get('/organizations/me/workspace-ai-context', {
      params: { workspace_id: workspaceId },
    });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to load workspace AI context' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(payload) };
  },

  async updateAiContext(
    payload: Partial<OrganizationAiContextStructured>,
    workspaceId: string,
  ): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const res: any = await apiClient.put('/organizations/me/workspace-ai-context', {
      ...payload,
      workspace_id: workspaceId,
    });
    if (res?.error || res?.success === false) {
      const errors =
        (res?.errors && typeof res.errors === 'object' ? res.errors : undefined) ??
        (res?.data?.errors && typeof res.data.errors === 'object' ? res.data.errors : undefined);
      return {
        ok: false,
        error: res?.error || 'Failed to update workspace AI context',
        errors,
      };
    }
    const body = res?.data?.data ?? res?.data ?? res;
    return { ok: true, data: safeAiContextResponse(body) };
  },

  async clearAiContext(workspaceId: string): Promise<OrganizationApiResult<OrganizationAiContextResponse>> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const params = new URLSearchParams({ workspace_id: workspaceId });
    const res: any = await apiClient.delete(`/organizations/me/workspace-ai-context?${params.toString()}`);
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to clear workspace AI context' };
    }
    return { ok: true, data: { hasContent: false, raw: '', parsed: {} as OrganizationAiContextStructured } };
  },
};

export interface BrandWebsite {
  website_url: string;
  site_name: string;
  colors: string[];
  description: string;
  logo_url: string;
  design_style: string;
  brand_tone: string;
  target_audience: string;
  suggested_tagline: string;
  created_at?: string;
  updated_at?: string;
}

export const brandWebsitesApi = {
  async list(workspaceId: string): Promise<{ ok: boolean; websites: BrandWebsite[] }> {
    if (!workspaceId) return { ok: false, websites: [] };
    const res: any = await apiClient.get('/organizations/me/brand-websites', {
      params: { workspace_id: workspaceId },
    });
    if (res?.error || res?.success === false) {
      return { ok: false, websites: [] };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return {
      ok: true,
      websites: Array.isArray(payload?.websites) ? payload.websites : [],
    };
  },

  async add(websiteUrl: string, workspaceId: string): Promise<{ ok: boolean; website?: BrandWebsite; error?: string }> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const res: any = await apiClient.post('/organizations/me/brand-websites', {
      website_url: websiteUrl,
      workspace_id: workspaceId,
    });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to analyze website' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, website: payload?.website };
  },

  async update(websiteUrl: string, updates: Partial<BrandWebsite>, workspaceId: string): Promise<{ ok: boolean; website?: BrandWebsite; error?: string }> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const encoded = btoa(websiteUrl);
    const res: any = await apiClient.put(`/organizations/me/brand-websites/${encoded}`, {
      ...updates,
      workspace_id: workspaceId,
    });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to update website' };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return { ok: true, website: payload };
  },

  async remove(websiteUrl: string, workspaceId: string): Promise<{ ok: boolean; error?: string }> {
    if (!workspaceId) return { ok: false, error: 'No workspace selected' };
    const encoded = btoa(websiteUrl);
    const params = new URLSearchParams({ workspace_id: workspaceId });
    const res: any = await apiClient.delete(`/organizations/me/brand-websites/${encoded}?${params.toString()}`);
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to remove website' };
    }
    return { ok: true };
  },
};

export const dailyLeadsEmailApi = {
  async getSettings(): Promise<{ ok: boolean; time: string | null; additionalEmails: string[] }> {
    const res: any = await apiClient.get('/organizations/me/daily-leads-email');
    if (res?.error || res?.success === false) {
      return { ok: false, time: null, additionalEmails: [] };
    }
    const payload = res?.data?.data ?? res?.data ?? res;
    return {
      ok: true,
      time: payload?.time ?? null,
      additionalEmails: Array.isArray(payload?.additionalEmails) ? payload.additionalEmails : [],
    };
  },

  async saveSettings(time: string | null, additionalEmails: string[]): Promise<{ ok: boolean; error?: string }> {
    const res: any = await apiClient.put('/organizations/me/daily-leads-email', { time, additionalEmails });
    if (res?.error || res?.success === false) {
      return { ok: false, error: res?.error || 'Failed to update schedule' };
    }
    return { ok: true };
  },
};
